import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  PhoneArrowDownLeftIcon,
  Cog6ToothIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { PhoneInput } from '../components/ui/PhoneInput';
import { COUNTRIES, PRIORITY_COUNTRIES } from '../data/countries';

function PhoneNumbers() {
  const { t } = useTranslation();
  const { user, tenant, loading: authLoading, can, isAuthenticated } = useAuth();
  const [numbers, setNumbers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingNumber, setEditingNumber] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [configuringTwilio, setConfiguringTwilio] = useState({});
  const [formData, setFormData] = useState({
    phone_number: '',
    label: '',
    tenant_id: '',
  });
  const [selectedCountry, setSelectedCountry] = useState(
    () => COUNTRIES.find(c => c.code === 'MX') || COUNTRIES[0]
  );
  const [rawNumber, setRawNumber] = useState('');
  const [editFormData, setEditFormData] = useState({ label: '' });

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (authLoading || !isAuthenticated || !user) return;

    // For tenant-scoped users, wait until tenant context is available to avoid
    // first-load auth/tenant race after login (especially Google Sign-In flow).
    if (!isSuperAdmin && !user?.tenant_id && !tenant?.tenant_id) return;

    fetchNumbers();
    fetchAgents();
    if (isSuperAdmin) {
      fetchTenants();
    }
  }, [authLoading, isAuthenticated, isSuperAdmin, user, tenant]);

  const fetchNumbers = async () => {
    setLoading(true);

    try {
      // Minimal reliability guard for first-load auth/session propagation:
      // retry transient auth/server/network failures a couple of times.
      const delaysMs = [0, 300, 700];
      let lastError = null;

      for (let attempt = 0; attempt < delaysMs.length; attempt += 1) {
        try {
          if (delaysMs[attempt] > 0) {
            await new Promise((resolve) => setTimeout(resolve, delaysMs[attempt]));
          }

          const response = await api.get('/phone-numbers');
          setNumbers(response.data);
          return;
        } catch (error) {
          lastError = error;
          const status = error?.response?.status;
          const isTransient = !status || status === 401 || status === 403 || status >= 500;

          if (!isTransient || attempt === delaysMs.length - 1) {
            break;
          }
        }
      }

      console.error('Error fetching phone numbers:', lastError);
      toast.error(t('phoneNumbers.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await api.get('/tenants');
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await api.get('/agents');
      setAgents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const handleInboundChange = async (phoneNumberId, agentId) => {
    try {
      await api.put(`/phone-numbers/${phoneNumberId}/inbound`, {
        agent_id: agentId || null,
      });
      toast.success(agentId ? t('phoneNumbers.inboundConfigured') : t('phoneNumbers.inboundRemoved'));
      setNumbers(prev =>
        prev.map(n =>
          n.phone_number_id === phoneNumberId ? { ...n, inbound_agent_id: agentId || '' } : n
        )
      );
    } catch (error) {
      console.error('Error updating inbound config:', error);
      toast.error(error.response?.data?.detail || t('phoneNumbers.failedInbound'));
    }
  };

  const handleInboundToggle = async (phoneNumberId, enabled) => {
    try {
      await api.put(`/phone-numbers/${phoneNumberId}/inbound-toggle`, { enabled });
      setNumbers(prev =>
        prev.map(n =>
          n.phone_number_id === phoneNumberId ? { ...n, inbound_enabled: enabled } : n
        )
      );
      toast.success(enabled ? t('phoneNumbers.inboundEnabled') : t('phoneNumbers.inboundPaused'));
    } catch (error) {
      console.error('Error toggling inbound:', error);
      toast.error(error.response?.data?.detail || t('phoneNumbers.failedInbound'));
    }
  };

  const handleConfigureTwilio = async (phoneNumberId) => {
    try {
      setConfiguringTwilio(prev => ({ ...prev, [phoneNumberId]: true }));
      await api.post(`/phone-numbers/${phoneNumberId}/configure-twilio`);
      toast.success(t('phoneNumbers.twilioConfigured'));
    } catch (error) {
      console.error('Error configuring Twilio:', error);
      toast.error(error.response?.data?.detail || t('phoneNumbers.failedConfigureTwilio'));
    } finally {
      setConfiguringTwilio(prev => ({ ...prev, [phoneNumberId]: false }));
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formData.phone_number || !formData.label) {
      toast.error(t('phoneNumbers.numberAndLabelRequired'));
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        phone_number: formData.phone_number,
        label: formData.label,
      };
      if (isSuperAdmin && formData.tenant_id) {
        payload.tenant_id = formData.tenant_id;
      }
      await api.post('/phone-numbers', payload);
      toast.success(t('phoneNumbers.added'));
      setShowAddModal(false);
      setFormData({ phone_number: '', label: '', tenant_id: '' });
      setRawNumber('');
      fetchNumbers();
    } catch (error) {
      console.error('Error adding phone number:', error);
      toast.error(error.response?.data?.detail || t('phoneNumbers.failedAdd'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditOpen = (num) => {
    setEditingNumber(num);
    setEditFormData({ label: num.label, tenant_id: num.tenant_id || '' });
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editFormData.label.trim()) {
      toast.error(t('phoneNumbers.numberAndLabelRequired'));
      return;
    }
    try {
      setSubmitting(true);
      const payload = { label: editFormData.label.trim() };
      if (isSuperAdmin && editFormData.tenant_id) {
        payload.tenant_id = editFormData.tenant_id;
      }
      await api.put(`/phone-numbers/${editingNumber.phone_number_id}`, payload);
      toast.success(t('phoneNumbers.updated'));
      setEditingNumber(null);
      fetchNumbers();
    } catch (error) {
      console.error('Error updating phone number:', error);
      toast.error(error.response?.data?.detail || t('phoneNumbers.failedUpdate'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (phoneNumberId) => {
    if (!window.confirm(t('phoneNumbers.deleteConfirm'))) return;
    try {
      await api.delete(`/phone-numbers/${phoneNumberId}`);
      toast.success(t('phoneNumbers.deleted'));
      fetchNumbers();
    } catch (error) {
      console.error('Error deleting phone number:', error);
      toast.error(error.response?.data?.detail || t('phoneNumbers.failedDelete'));
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between mb-8">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-foreground sm:text-3xl sm:truncate">
            {t('phoneNumbers.title')}
          </h2>
          <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>
            {t('phoneNumbers.subtitle')}
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            onClick={() => setShowAddModal(true)}
            className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            {t('phoneNumbers.addNumber')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-8">
        <div className="bg-card overflow-hidden shadow rounded-xl">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <PhoneIcon className="h-6 w-6 text-green-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium truncate" style={{ color: 'var(--muted)' }}>{t('phoneNumbers.totalNumbers')}</dt>
                  <dd className="text-lg font-semibold text-foreground">{numbers.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-card overflow-hidden shadow rounded-xl">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <PhoneArrowDownLeftIcon className="h-6 w-6 text-blue-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium truncate" style={{ color: 'var(--muted)' }}>{t('phoneNumbers.inboundActive')}</dt>
                  <dd className="text-lg font-semibold text-foreground">
                    {numbers.filter(n => n.inbound_agent_id && n.inbound_enabled !== false).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        {isSuperAdmin && (
          <div className="bg-card overflow-hidden shadow rounded-xl">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BuildingOfficeIcon className="h-6 w-6 text-purple-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium truncate" style={{ color: 'var(--muted)' }}>{t('phoneNumbers.enterprises')}</dt>
                    <dd className="text-lg font-semibold text-foreground">
                      {new Set(numbers.map(n => n.tenant_id)).size}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Numbers Table */}
      <div className="bg-card shadow overflow-hidden sm:rounded-xl">
        <div className="px-4 py-5 sm:px-6 border-b border-border">
          <h3 className="text-lg leading-6 font-medium text-foreground">{t('phoneNumbers.managedNumbers')}</h3>
        </div>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : numbers.length === 0 ? (
          <div className="text-center py-12">
            <PhoneIcon className="mx-auto h-12 w-12" style={{ color: 'var(--muted)' }} />
            <h3 className="mt-2 text-sm font-medium text-foreground">{t('phoneNumbers.noNumbers')}</h3>
            <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>{t('phoneNumbers.getStarted')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-background">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                    {t('phoneNumbers.phoneNumber')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                    {t('phoneNumbers.label')}
                  </th>
                  {isSuperAdmin && (
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                      {t('phoneNumbers.enterprise')}
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                    {t('phoneNumbers.inboundAgent')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                    {t('phoneNumbers.dateAdded')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {numbers.map((num) => (
                  <tr key={num.phone_number_id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <PhoneIcon className="h-5 w-5 text-green-500 mr-2" />
                        <span className="text-sm font-medium text-foreground">{num.number}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-foreground">{num.label}</span>
                    </td>
                    {isSuperAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <BuildingOfficeIcon className="h-4 w-4 mr-1.5" style={{ color: 'var(--muted)' }} />
                          <span className="text-sm text-foreground">{num.tenant_name || '-'}</span>
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {/* Inbound on/off toggle */}
                        {num.inbound_agent_id && (
                          <button
                            onClick={() => handleInboundToggle(num.phone_number_id, num.inbound_enabled === false)}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              num.inbound_enabled !== false ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                            }`}
                            title={num.inbound_enabled !== false ? t('phoneNumbers.inboundOn') : t('phoneNumbers.inboundOff')}
                          >
                            <span
                              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                num.inbound_enabled !== false ? 'translate-x-4' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        )}
                        <select
                          value={num.inbound_agent_id || ''}
                          onChange={(e) => handleInboundChange(num.phone_number_id, e.target.value)}
                          className="text-sm border border-border rounded-md py-1 px-2 bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                          <option value="">{t('phoneNumbers.inboundDisabled')}</option>
                          {agents
                            .filter(a => isSuperAdmin || a.tenant_id === num.tenant_id || a.is_shared)
                            .map(a => (
                              <option key={a.id} value={a.id}>{a.name}</option>
                            ))}
                        </select>
                        {num.inbound_agent_id && (
                          <button
                            onClick={() => handleConfigureTwilio(num.phone_number_id)}
                            disabled={configuringTwilio[num.phone_number_id]}
                            className="flex items-center text-blue-500 hover:text-blue-700 disabled:opacity-50"
                            title={t('phoneNumbers.configureTwilio')}
                          >
                            <Cog6ToothIcon className={`h-4 w-4 ${configuringTwilio[num.phone_number_id] ? 'animate-spin' : ''}`} />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--muted)' }}>
                      {num.created_at ? new Date(num.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEditOpen(num)}
                          className="text-blue-600 hover:text-blue-900"
                          title={t('phoneNumbers.editNumber')}
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(num.phone_number_id)}
                          className="text-red-600 hover:text-red-900"
                          title={t('common.delete')}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Number Modal */}
      {showAddModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 text-center">
            <div className="fixed inset-0 transition-opacity" style={{ backgroundColor: 'var(--overlay)' }} onClick={() => setShowAddModal(false)} />
            <div className="relative bg-card rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button onClick={() => setShowAddModal(false)} className="hover:opacity-70" style={{ color: 'var(--muted)' }}>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-foreground mb-4">{t('phoneNumbers.addNumber')}</h3>
                  <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>
                    {t('phoneNumbers.addNumberDesc')}
                  </p>
                  <form onSubmit={handleAdd} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">{t('phoneNumbers.phoneNumber')}</label>
                      <div className="flex">
                        <select
                          value={selectedCountry.code}
                          onChange={(e) => {
                            const country = COUNTRIES.find(c => c.code === e.target.value);
                            if (country) {
                              setSelectedCountry(country);
                              setFormData(prev => ({ ...prev, phone_number: rawNumber ? `${country.dialCode}${rawNumber}` : '' }));
                            }
                          }}
                          className="rounded-l-lg border border-r-0 border-border bg-card text-sm px-2 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          {PRIORITY_COUNTRIES.map(code => {
                            const c = COUNTRIES.find(x => x.code === code);
                            return c ? <option key={c.code} value={c.code}>{c.flag} {c.dialCode}</option> : null;
                          })}
                          <option disabled>───</option>
                          {COUNTRIES.filter(c => !PRIORITY_COUNTRIES.includes(c.code)).map(c => (
                            <option key={c.code} value={c.code}>{c.flag} {c.name} {c.dialCode}</option>
                          ))}
                        </select>
                        <input
                          type="tel"
                          required
                          value={rawNumber}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/[^\d]/g, '');
                            setRawNumber(digits);
                            setFormData(prev => ({ ...prev, phone_number: digits ? `${selectedCountry.dialCode}${digits}` : '' }));
                          }}
                          onPaste={(e) => {
                            const pasted = e.clipboardData.getData('text').trim();
                            if (pasted.startsWith('+')) {
                              e.preventDefault();
                              const sorted = [...COUNTRIES].sort((a, b) => b.dialCode.length - a.dialCode.length);
                              const match = sorted.find(c => pasted.startsWith(c.dialCode));
                              if (match) {
                                setSelectedCountry(match);
                                const national = pasted.slice(match.dialCode.length).replace(/[^\d]/g, '');
                                setRawNumber(national);
                                setFormData(prev => ({ ...prev, phone_number: `${match.dialCode}${national}` }));
                              }
                            }
                          }}
                          placeholder="5512345678"
                          className="flex-1 rounded-r-lg border border-border bg-card text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      {formData.phone_number && (
                        <p className="mt-1 text-xs font-mono" style={{ color: 'var(--muted)' }}>{formData.phone_number}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">{t('phoneNumbers.label')}</label>
                      <input
                        type="text"
                        required
                        value={formData.label}
                        onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
                        placeholder={t('phoneNumbers.labelPlaceholder')}
                      />
                    </div>
                    {isSuperAdmin && (
                      <div>
                        <label className="block text-sm font-medium text-foreground">{t('phoneNumbers.enterprise')}</label>
                        <select
                          value={formData.tenant_id}
                          onChange={(e) => setFormData(prev => ({ ...prev, tenant_id: e.target.value }))}
                          className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
                        >
                          <option value="">{t('phoneNumbers.selectEnterprise')}</option>
                          {tenants.map(tenant => (
                            <option key={tenant.tenant_id} value={tenant.tenant_id}>
                              {tenant.business_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full inline-flex justify-center rounded-full border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                      >
                        {submitting ? t('common.loading') : t('phoneNumbers.addNumber')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAddModal(false)}
                        className="mt-3 w-full inline-flex justify-center rounded-full border border-border shadow-sm px-4 py-2 bg-card text-base font-medium text-foreground hover:bg-background focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Edit Number Modal */}
      {editingNumber && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 text-center">
            <div className="fixed inset-0 transition-opacity" style={{ backgroundColor: 'var(--overlay)' }} onClick={() => setEditingNumber(null)} />
            <div className="relative bg-card rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button onClick={() => setEditingNumber(null)} className="hover:opacity-70" style={{ color: 'var(--muted)' }}>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-foreground mb-4">{t('phoneNumbers.editNumber')}</h3>
                  <p className="text-sm mb-2" style={{ color: 'var(--muted)' }}>
                    {t('phoneNumbers.editNumberDesc')}
                  </p>
                  <p className="text-sm font-medium text-foreground mb-4">{editingNumber.number}</p>
                  <form onSubmit={handleEditSave} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground">{t('phoneNumbers.label')}</label>
                      <input
                        type="text"
                        required
                        value={editFormData.label}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, label: e.target.value }))}
                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
                        placeholder={t('phoneNumbers.labelPlaceholder')}
                      />
                    </div>
                    {isSuperAdmin && (
                      <div>
                        <label className="block text-sm font-medium text-foreground">{t('phoneNumbers.enterprise')}</label>
                        <select
                          value={editFormData.tenant_id}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, tenant_id: e.target.value }))}
                          className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
                        >
                          <option value="">{t('phoneNumbers.selectEnterprise')}</option>
                          {tenants.map(tenant => (
                            <option key={tenant.tenant_id} value={tenant.tenant_id}>
                              {tenant.business_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full inline-flex justify-center rounded-full border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                      >
                        {submitting ? t('common.loading') : t('common.save')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingNumber(null)}
                        className="mt-3 w-full inline-flex justify-center rounded-full border border-border shadow-sm px-4 py-2 bg-card text-base font-medium text-foreground hover:bg-background focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PhoneNumbers;
